import React, {
  forwardRef,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import cls from "@soluto-design/styles/cls";
import VideoWrapper, {
  VideoWrapperRef,
} from "design-system/src/components/atoms/VideoWrapper";
import { MediaProps } from "../../molecules/Media";
import { PictureProps } from "@soluto-design/picture";

export interface VideoSourceProps {
  src: string;
  type: "webm" | "mp4" | "mov";
}

export interface VideoProps {
  classes?: {
    wrapper?: string;
    video?: string;
  };
  sources?: VideoSourceProps[];
  id?: string;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  controls?: boolean;
  autoPlay?: boolean;
  lazy?: boolean;
  poster?: PictureProps;
  className?: string;
  layout?: MediaProps["layout"];
}

const Video = forwardRef(function Video(
  {
    sources = [],
    classes,
    className,
    id,
    loop,
    muted,
    playsInline,
    controls,
    autoPlay,
    lazy,
    poster,
    layout,
  }: VideoProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const [showControls, setShowControls] = useState(autoPlay ? false : controls);

  const refVideo = useRef(null);
  const wrapperRef = useRef<VideoWrapperRef>();

  const playVideo = useCallback(() => {
    if (refVideo?.current) {
      (refVideo as any).current.play();
    }
  }, [refVideo]);

  const onVideoPlay = useCallback(() => {
    if (autoPlay) return;

    wrapperRef.current?.setPlayed(true);
    setShowControls(true);
  }, [wrapperRef, autoPlay]);

  const commonProps = {
    loop: autoPlay ? true : loop,
    muted: autoPlay ? true : muted,
    playsInline: autoPlay ? true : playsInline,
    autoPlay,
    preload: "auto",
    id,
    ...(lazy && { "data-lazy": "true" }),
  };

  useEffect(() => {
    if (typeof document !== "undefined" && lazy && sources?.length > 0) {
      if ("IntersectionObserver" in window) {
        // Added for lazy loading videos
        let lazyVideoObserver = new IntersectionObserver(function (
          entries,
          observer
        ) {
          entries.forEach(function (video) {
            if (video.isIntersecting) {
              for (let source in video.target.children) {
                let videoSource = video.target.children[
                  source
                ] as HTMLSourceElement;
                if (
                  typeof videoSource.tagName === "string" &&
                  videoSource.tagName === "SOURCE" &&
                  videoSource.dataset.src
                ) {
                  videoSource.src = videoSource.dataset.src;
                }
              }

              (video.target as HTMLVideoElement).load();
              video.target.removeAttribute("data-lazy");
              lazyVideoObserver.unobserve(video.target);
            }
          });
        });

        if (refVideo?.current) {
          lazyVideoObserver.observe(refVideo?.current);
        }
      }
    }
  }, [sources, lazy]);

  return (
    <VideoWrapper
      ref={wrapperRef}
      onClickStart={playVideo}
      {...(!autoPlay && {
        showPlayButton: true,
        poster,
      })}
      layout={layout}
      className={cls(className, classes?.wrapper)}
    >
      <video
        allowFullScreen
        {...commonProps}
        controls={showControls}
        className={cls(
          classes?.video,
          layout === "responsive"
            ? "w-full h-auto"
            : "absolute inset-0 w-full h-full object-cover"
        )}
        onPlay={onVideoPlay}
        ref={refVideo}
      >
        {sources?.length > 0 &&
          sources.map((s) => (
            <source
              type={`video/${s.type}`}
              key={`video/${s.type}`}
              {...(lazy ? { "data-src": s?.src } : { src: s?.src })}
            />
          ))}
      </video>
    </VideoWrapper>
  );
});

export default Video;
